import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

const cardVariant = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5 } },
};

const containerVariant = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.12, delayChildren: 0.05 },
  },
};

const listItemVariant = {
  hidden: { opacity: 0, x: -8 },
  visible: { opacity: 1, x: 0, transition: { duration: 0.3 } },
};


const ComingSoon: React.FC = () => {
  const { t } = useTranslation('comingSoon');
  const sections = t('sections', { returnObjects: true }) as Array<{ icon: string; title: string; items: string[] }>;
  return (
        <div className="animate-gradient-bg relative flex min-h-screen items-center justify-center overflow-hidden px-6 py-20 text-center">

          {/* Dekorativní animované blob prvky */}
          <span className="absolute -top-40 -left-20 w-80 h-80 bg-primary-600 rounded-full mix-blend-overlay opacity-30 filter blur-3xl animate-blob animate-float"></span>
          <span className="absolute top-20 right-0 w-72 h-72 bg-secondary-500 rounded-full mix-blend-overlay opacity-20 filter blur-3xl animate-blob animation-delay-2000 animate-float"></span>
          <span className="absolute bottom-0 left-1/2 transform -translate-x-1/2 w-96 h-96 bg-purple-500 rounded-full mix-blend-overlay opacity-15 filter blur-3xl animate-blob animation-delay-4000 animate-float"></span>
       <motion.div
         initial={{ opacity: 0, y: 30 }}
         animate={{ opacity: 1, y: 0 }}
         transition={{ duration: 0.8 }}
         className="max-w-6xl mx-auto relative"
       >

        <motion.div
          role="alert"
          aria-live="polite"
          aria-labelledby="comingsoon-alert-title"
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.15 }}
          className="relative mb-10 max-w-4xl mx-auto rounded-2xl text-sm md:text-base shadow-xl backdrop-blur-xl bg-gradient-to-br from-dark-800/80 to-dark-700/80 ring-1 ring-yellow-400/25 border border-yellow-500/20"
        >
          <span className="absolute left-0 top-0 bottom-0 w-1.5 bg-gradient-to-b from-yellow-400/80 to-yellow-500/30 rounded-l-2xl" />
          <span className="pointer-events-none absolute inset-x-4 -top-px h-px bg-gradient-to-r from-transparent via-yellow-400/40 to-transparent" />
          <motion.span
            aria-hidden
            initial={{ x: -200, opacity: 0 }}
            animate={{ x: 200, opacity: [0, 1, 0] }}
            transition={{ duration: 2.2, repeat: Infinity, repeatDelay: 1.2 }}
            className="pointer-events-none absolute top-0 bottom-0 w-1/3 -translate-x-1/2 bg-gradient-to-r from-transparent via-yellow-300/10 to-transparent rounded-2xl"
          />
          <div className="relative p-5 md:p-6">
            <div className="flex items-center gap-3 mb-2">
              <motion.span
                aria-hidden
                animate={{ scale: [1, 1.05, 1] }}
                transition={{ duration: 2, repeat: Infinity }}
                className="inline-flex items-center justify-center w-9 h-9 rounded-xl bg-yellow-500/15 ring-1 ring-yellow-400/40 text-yellow-200"
              >
                ⚠️
              </motion.span>
              <span id="comingsoon-alert-title" className="px-2 py-1 rounded-md text-[11px] md:text-xs uppercase tracking-wide bg-yellow-500/15 text-yellow-200 ring-1 ring-yellow-400/30">{t('alertTitle')}</span>
            </div>
            <p className="text-yellow-100 leading-relaxed">
              {t('alertText')}
            </p>
          </div>
          <span className="pointer-events-none absolute inset-x-0 -bottom-px h-px bg-gradient-to-r from-transparent via-yellow-400/40 to-transparent" />
        </motion.div>
        <div className="mb-2 inline-flex items-center gap-2 rounded-full bg-dark-700/60 px-4 py-2 text-dark-300 shadow-md ring-1 ring-dark-600/60">
          <span className="h-2 w-2 animate-pulse rounded-full bg-primary-500" />
          <span>{t('preparing')}</span>
        </div>

        <h1 className="mb-3 font-display text-5xl font-bold gradient-text md:text-6xl">
          {t('mainHeading')}
        </h1>
        <div className="section-divider mx-auto mb-6 h-1 max-w-xs rounded-full bg-gradient-to-r from-primary-500/20 via-secondary-500/20 to-primary-500/20" />
        <p className="text-xl text-dark-300 mb-10 leading-relaxed max-w-3xl mx-auto">
          {t('introText')}
        </p>

        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.2 }}
          variants={containerVariant}
          className="grid gap-8 sm:grid-cols-2 lg:grid-cols-3 mb-16"
        >
          {sections.map((s, idx) => (
            <motion.div
              key={idx}
              variants={cardVariant}
              whileHover={{ scale: 1.025, rotate: 0.35 }}
              className="group relative overflow-hidden p-6 bg-dark-800/70 border border-dark-700 rounded-2xl backdrop-blur-xl shadow-xl hover:shadow-primary-600/30 transition-all duration-300 ring-1 ring-dark-700/60 hover:ring-primary-500/40"
            >
              <span className="absolute -top-24 -left-24 w-56 h-56 bg-gradient-to-br from-primary-500/10 to-secondary-500/10 rounded-full blur-3xl opacity-0 group-hover:opacity-100 transition-opacity" />
              <span className="absolute -bottom-24 -right-24 w-56 h-56 bg-gradient-to-br from-secondary-500/10 to-primary-500/10 rounded-full blur-3xl opacity-0 group-hover:opacity-100 transition-opacity" />

              <div className="relative flex items-center mb-3">
                <motion.span
                  aria-hidden
                  animate={{ rotate: [0, 6, 0] }}
                  transition={{ duration: 3, repeat: Infinity }}
                  className="inline-flex items-center justify-center w-9 h-9 rounded-xl bg-gradient-to-br from-primary-500/20 to-secondary-500/20 text-xl shadow-inner ring-1 ring-dark-600/50"
                >
                  {s.icon}
                </motion.span>
                <div className="ml-3">
                  <h3 className="text-xl font-semibold tracking-tight">
                    {s.title}
                  </h3>
                  <span className="mt-1 block h-px w-24 bg-gradient-to-r from-transparent via-primary-500/40 to-transparent" />
                </div>
              </div>

              <ul className="list-disc list-inside space-y-2 text-sm leading-relaxed marker:text-primary-400/80">
                {s.items.map((text, i) => (
                  <motion.li key={i} variants={listItemVariant} className="">
                    {text}
                  </motion.li>
                ))}
              </ul>
              <span className="pointer-events-none absolute inset-x-0 -bottom-px h-px bg-gradient-to-r from-transparent via-primary-500/25 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
            </motion.div>
          ))}
        </motion.div>

        <Link to="/">
          <motion.button
            whileHover={{ scale: 1.03, boxShadow: '0 20px 35px rgba(168, 85, 247, 0.2)' }}
            whileTap={{ scale: 0.96 }}
            className="relative overflow-hidden px-10 py-4 bg-gradient-to-r from-secondary-600 to-primary-600 rounded-2xl font-semibold text-purple-200 shadow-xl hover:shadow-secondary-500/30 transition-all duration-200"
          >
            <span className="absolute inset-0 opacity-0 hover:opacity-20 transition-opacity bg-[radial-gradient(ellipse_at_center,rgba(255,255,255,0.6),transparent_40%)]" />
            {t('backButton')}
          </motion.button>
        </Link>
      </motion.div>
    </div>
  );
};

export default ComingSoon;
